/**
 * Load Board generic change audit types.
 * Tracks CHANGES ONLY (old → new), not first-time assignments.
 * Assign Door: door priority, door usage, door type, trailer, driver, carrier.
 * Dispatch: trailer change only.
 * Close Door / Departure: not audited in this UI.
 */

export type LoadBoardChangeActionType = "ASSIGN_DOOR" | "DISPATCH";

export type LoadBoardChangeFieldName =
  | "CARRIER"
  | "TRAILER"
  | "DOOR_PRIORITY"
  | "DOOR_USAGE"
  | "DOOR_TYPE"
  | "DRIVER"
  | string;

export interface LoadBoardChangeLogEntry {
  id: number;
  manifestNumber: number;
  tripNumber?: number | null;
  cutDate?: string | null;
  terminalNumber?: number | null;
  actionType: LoadBoardChangeActionType;
  fieldName: string;
  previousValue?: string | null;
  newValue?: string | null;
  reasonCode?: string | null;
  reasonDescription?: string | null;
  changedAt: string;
  modifiedBy: string;
}

export interface LoadBoardChangeLogFilterParams {
  manifestNumber?: number;
  terminalNumber?: number;
  actionType?: LoadBoardChangeActionType;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  size?: number;
}

const FIELD_LABELS: Record<string, string> = {
  CARRIER: "Carrier",
  TRAILER: "Trailer",
  DOOR_PRIORITY: "Door priority",
  DOOR_USAGE: "Door usage",
  DOOR_TYPE: "Door type",
  DRIVER: "Driver",
};

export const formatFieldLabel = (fieldName: string): string => {
  if (FIELD_LABELS[fieldName]) return FIELD_LABELS[fieldName];
  return fieldName
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const formatActionLabel = (actionType: LoadBoardChangeActionType): string => {
  switch (actionType) {
    case "ASSIGN_DOOR":
      return "Assign Door";
    case "DISPATCH":
      return "Dispatch";
    default:
      return actionType;
  }
};

/**
 * Display helper for previous/new values.
 * Driver may be stored as "id|name" — show "name (id)" when both present.
 */
export const formatChangeValue = (
  value?: string | null,
  fieldName?: string
): string => {
  if (value == null || value === "") return "—";
  if (fieldName === "DRIVER" && value.includes("|")) {
    const [id, ...nameParts] = value.split("|");
    const name = nameParts.join("|").trim();
    if (id && name) return `${name} (${id})`;
    if (name) return name;
    if (id) return id;
  }
  return value;
};
